import Axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import AdminLeftMenu from "../../Navbar/AdminLeftMenu";
import AdminHeader from "../../Header/AdminHeader";
import styles from "./CdpSearch.module.css";
import {
  FiInfo,
  FiSearch,
  FiX,
  FiCopy,
  FiCheck,
  FiFileText,
  FiUser,
  FiServer,
  FiMessageCircle,
} from "react-icons/fi";

const CDP_API_URL = process.env.REACT_APP_CDP_SEARCH_API_URL;
const PAGE_LIMIT = 50;

const lStorage =
  JSON.parse(localStorage.getItem("login")) ||
  JSON.parse(localStorage.getItem("admin")) ||
  JSON.parse(localStorage.getItem("emp")) ||
  JSON.parse(localStorage.getItem("reseller"));

if (lStorage != null) {
  var userId = lStorage?.user?.userId;
  var userToken = lStorage?.token;
}

const getInitials = (name = "") => {
  const clean = name.includes("@") ? name.split("@")[0] : name;
  return clean.slice(0, 2).toUpperCase();
};

const isEmpty = (val) =>
  val === null ||
  val === undefined ||
  val === "" ||
  val === "NA" ||
  (Array.isArray(val) && val.length === 0);

const humanize = (key = "") =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/* ── Event options ── */
const EVENT_OPTIONS = [
  { label: "Choose an event…", value: "" },
  { label: "Ticket Assigned", value: "ticket_assigned" },
  { label: "Conversation", value: "conversation" },
];

/* ── Copy button ── */
const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (isEmpty(value)) return null;

  const handleCopy = (e) => {
    e.stopPropagation();
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(String(value));
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <button
      type="button"
      className={styles.copyIconBtn}
      onClick={handleCopy}
      aria-label="Copy value"
    >
      {copied ? <FiCheck size={13} /> : <FiCopy size={13} />}
    </button>
  );
};

/* ── Field cell – only renders when value exists ── */
const ModalField = ({ label, value, copyable, full, badge }) => {
  if (isEmpty(value)) return null;

  return (
    <div className={`${styles.modalField} ${full ? styles.modalFieldFull : ""}`}>
      <div className={styles.modalFieldLabel}>{label}</div>
      <div className={styles.modalFieldRow}>
        <span className={styles.modalFieldValue}>{String(value)}</span>
        {badge && !isEmpty(badge) && (
          <span className={styles.modalFieldBadge}>{badge}</span>
        )}
        {copyable && <CopyButton value={value} />}
      </div>
    </div>
  );
};

/* ── Section header ── */
const SectionHeader = ({ icon, children }) => (
  <div className={styles.modalSectionHeader}>
    {icon}
    <span>{children}</span>
  </div>
);

/* ── Renders any plain object as a grid of fields ── */
const ObjectGrid = ({ data, exclude = [] }) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;

  const entries = Object.entries(data).filter(
    ([k, v]) => !exclude.includes(k) && !isEmpty(v)
  );

  if (entries.length === 0) return null;

  return (
    <div className={styles.modalGrid}>
      {entries.map(([key, value]) => {
        // skip pure *_id when a matching *_name exists (shown as badge)
        if (key.endsWith("_id")) {
          const nameKey = key.replace(/_id$/, "_name");
          if (data[nameKey] !== undefined) return null;
        }

        const base = key.replace(/_name$/, "");
        const idValue = data[`${base}_id`];
        const badge = idValue ? `ID: ${idValue}` : null;

        return (
          <ModalField
            key={key}
            label={humanize(key)}
            value={value}
            badge={badge}
            copyable={["email", "mobile", "brand_number"].includes(key)}
            full={["email", "ticket_name", "message_content"].includes(key)}
          />
        );
      })}
    </div>
  );
};

/* ── Detail Modal (fully generic) ── */
const CustomerModal = ({ item, onClose }) => {
  if (!item) return null;

  const recordId = item._id || item.record_id;
  const eventName = item.event_name || "";
  const rawEventData = item.event_data;
  const isArrayEventData = Array.isArray(rawEventData);
  const eventDataObj = !isArrayEventData ? rawEventData || {} : {};
  const eventDataList = isArrayEventData ? rawEventData : [];

  const messageContent = eventDataObj.message_content;
  const messageTimestamp = eventDataObj.created || item.created;

  const CONTACT_KEYS = [
    "mobile",
    "brand_number",
    "country_code",
    "user_type",
    "email",
    "user_id",
  ];

  const SYSTEM_KEYS = ["event_source", "created", "event_name"];

  const TOP_LEVEL_EXCLUDE = [
    "_id",
    "record_id",
    "event_data",
    "event_name",
    "score",
    "is_active",
    "name", // already in header
  ];

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <div className={styles.modalAvatar}>
              {getInitials(item.name || item.email)}
            </div>
            <div className={styles.modalHeaderText}>
              <div className={styles.modalNameRow}>
                <p className={styles.modalName}>
                  {item.name || item.email || "—"}
                </p>
              </div>
              <p className={styles.modalMeta}>
                {!isEmpty(recordId) && <>Record ID: {recordId}</>}
                {!isEmpty(item.event_source) && (
                  <>
                    <span className={styles.modalMetaDot}>•</span>
                    {item.event_source}
                  </>
                )}
                {!isEmpty(eventName) && (
                  <>
                    <span className={styles.modalMetaDot}>•</span>
                    {eventName}
                  </>
                )}
              </p>
            </div>
          </div>
          <div className={styles.modalHeaderRight}>
            <button
              className={styles.modalCloseBtn}
              onClick={onClose}
              aria-label="Close"
            >
              <FiX size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.modalTabs}>
          <button
            type="button"
            className={`${styles.modalTabBtn} ${styles.modalTabBtnActive}`}
          >
            Structured Overview
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Latest Message (conversation) */}
          {!isEmpty(messageContent) && (
            <div className={styles.messageCallout}>
              <div className={styles.messageCalloutHeader}>
                <span className={styles.messageCalloutLabel}>
                  <FiMessageCircle size={13} />
                  Latest Message Content
                </span>
                <div className={styles.messageCalloutTags}>
                  {!isEmpty(eventDataObj.message_type) && (
                    <span className={styles.metaTag}>
                      {eventDataObj.message_type}
                    </span>
                  )}
                  {!isEmpty(eventDataObj.req_from) && (
                    <span className={styles.metaTag}>
                      {eventDataObj.req_from}
                    </span>
                  )}
                </div>
              </div>
              <p className={styles.messageCalloutText}>{messageContent}</p>
              <div className={styles.messageCalloutFooter}>
                <span className={styles.messageCalloutTimestamp}>
                  {!isEmpty(messageTimestamp)
                    ? `Timestamp: ${messageTimestamp}`
                    : ""}
                </span>
              </div>
            </div>
          )}

          {/* Contact & Identity */}
          <SectionHeader icon={<FiUser size={12} />}>
            Contact &amp; Identity
          </SectionHeader>
          <div className={styles.modalGrid}>
            {CONTACT_KEYS.map((key) => (
              <ModalField
                key={key}
                label={humanize(key)}
                value={item[key]}
                copyable={["email", "mobile", "brand_number"].includes(key)}
                full={key === "email"}
              />
            ))}
          </div>

          {/* Event Data – array (e.g. ticket_assigned) */}
          {isArrayEventData && eventDataList.length > 0 && (
            <>
              <SectionHeader icon={<FiFileText size={12} />}>
                Event Data ({eventDataList.length} item
                {eventDataList.length > 1 ? "s" : ""})
              </SectionHeader>
              {eventDataList.map((entry, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: 14,
                    padding: 12,
                    background: "#f9fafb",
                    borderRadius: 10,
                    border: "1px solid #f0f1f5",
                  }}
                >
                  {eventDataList.length > 1 && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6b7280",
                        marginBottom: 8,
                      }}
                    >
                      Item {idx + 1}
                    </div>
                  )}
                  <ObjectGrid data={entry} />
                </div>
              ))}
            </>
          )}

          {/* Event Data – object (e.g. conversation) */}
          {!isArrayEventData && Object.keys(eventDataObj).length > 0 && (
            <>
              <SectionHeader icon={<FiFileText size={12} />}>
                Event Data
              </SectionHeader>
              <ObjectGrid
                data={eventDataObj}
                exclude={["message_content"]} // already shown above
              />
            </>
          )}

          {/* System & Ingestion Meta */}
          <SectionHeader icon={<FiServer size={12} />}>
            System &amp; Ingestion Meta
          </SectionHeader>
          <div className={styles.modalGrid}>
            {SYSTEM_KEYS.map((key) => (
              <ModalField
                key={key}
                label={humanize(key)}
                value={item[key]}
              />
            ))}
          </div>

          {/* Any remaining top-level keys */}
          {(() => {
            const extra = Object.fromEntries(
              Object.entries(item).filter(
                ([k, v]) =>
                  !TOP_LEVEL_EXCLUDE.includes(k) &&
                  !CONTACT_KEYS.includes(k) &&
                  !SYSTEM_KEYS.includes(k) &&
                  !isEmpty(v)
              )
            );
            if (Object.keys(extra).length === 0) return null;
            return (
              <>
                <SectionHeader icon={<FiInfo size={12} />}>
                  Other Fields
                </SectionHeader>
                <ObjectGrid data={extra} />
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

/* ── Info Popover ── */
const InfoPopover = ({ onClose }) => (
  <div className={styles.infoOverlay} onClick={onClose}>
    <div className={styles.infoPopover} onClick={(e) => e.stopPropagation()}>
      <div className={styles.infoPopoverHeader}>
        <span className={styles.infoPopoverTitle}>About this page</span>
        <button
          className={styles.modalCloseBtn}
          onClick={onClose}
          aria-label="Close"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <p className={styles.infoPopoverText}>
        The Customer Data Platform lets you find customer records tied to a
        specific event — no filters or query writing needed. Pick an event from
        the dropdown, optionally narrow it down with a search query, and matching
        records load automatically.
      </p>
      <ul className={styles.infoPopoverList}>
        <li>Choose an event from the dropdown to run the search.</li>
        <li>Type a query in the search box to filter results further.</li>
        <li>Click any card to see the customer's full profile.</li>
        <li>Scroll down to load more results automatically.</li>
      </ul>
    </div>
  </div>
);

/* ── Main Component ── */
const CdpSearch = () => {
  const [selectedEvent, setSelectedEvent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [apiError, setApiError] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const loaderRef = useRef(null);

  const fetchData = async (eventName, query = "") => {
    if (!eventName) return;
    setLoading(true);
    setSearched(true);
    setApiError("");
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setTotalResults(0);

    try {
      const { data } = await Axios.post(
        CDP_API_URL,
        {
          method: "queryCdp",
          question: query,
          event_name: eventName,
          limit: PAGE_LIMIT,
          offset: 0,
          user_id: userId,
          token: userToken,
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json; charset=utf-8",
          },
        }
      );
      if (data.success) {
        setResults(data.data || []);
        setHasMore(!!data.hasMore);
        setTotalResults(data.totalResults || 0);
        setOffset((data.data || []).length);
      } else {
        setApiError(data.message || "Search failed. Please try again.");
      }
    } catch (error) {
      setApiError(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchMore = async () => {
    if (!hasMore || loadingMore || loading || !selectedEvent) return;
    setLoadingMore(true);
    try {
      const { data } = await Axios.post(
        CDP_API_URL,
        {
          method: "queryCdp",
          question: searchQuery,
          event_name: selectedEvent,
          limit: PAGE_LIMIT,
          offset,
          user_id: userId,
          token: userToken,
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json; charset=utf-8",
          },
        }
      );

      if (data.success) {
        setResults((prev) => [...prev, ...(data.data || [])]);
        setHasMore(!!data.hasMore);
        setOffset((prev) => prev + (data.data || []).length);
      }
    } catch (error) {
      console.error("Pagination error:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, offset, selectedEvent]);

  const handleEventChange = (e) => {
    const value = e.target.value;
    setSelectedEvent(value);
    if (apiError) setApiError("");

    if (!value) {
      setResults([]);
      setSearched(false);
      setHasMore(false);
      setTotalResults(0);
      return;
    }
    fetchData(value, searchQuery);
  };

  const handleSearchSubmit = () => {
    if (!selectedEvent) {
      setApiError("Choose an event before searching.");
      return;
    }
    fetchData(selectedEvent, searchQuery);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    if (selectedEvent) fetchData(selectedEvent, "");
  };

  return (
    <div className="wrapper">
      <AdminLeftMenu />
      <div className="main">
        <AdminHeader />
        <main className="content">
          <div className={styles.searchPage}>
            <div className={styles.topSection}>
              {/* Page Header */}
              <div className={styles.pageHeader}>
                <div className={styles.pageHeaderLeft}>
                  <h1 className={styles.pageTitle}>Customer Data Platform</h1>
                  <button
                    className={styles.infoBtn}
                    onClick={() => setShowInfo(true)}
                    aria-label="About this page"
                  >
                    <FiInfo size={16} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
              <p className={styles.pageSubtitle}>
                Select an entity type or write a natural language query to find
                matching customer records
              </p>

              {/* Search Row */}
              <div className={styles.searchRow}>
                <div className={styles.searchInputWrap}>
                  <FiSearch className={styles.searchInputIcon} size={16} />
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Search by name, email, mobile…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    aria-label="Search query"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className={styles.searchInputClear}
                      onClick={handleClearSearch}
                      aria-label="Clear search"
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>

                <select
                  className={styles.presetSelect}
                  value={selectedEvent}
                  onChange={handleEventChange}
                  aria-label="Select event"
                >
                  {EVENT_OPTIONS.map((ev) => (
                    <option key={ev.value} value={ev.value}>
                      {ev.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className={styles.searchBtn}
                  onClick={handleSearchSubmit}
                  disabled={loading}
                >
                  Search
                </button>
              </div>

              {/* API Error */}
              {apiError && (
                <div className={styles.errorBanner}>
                  <svg
                    className={styles.errorBannerIcon}
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4M12 16h.01" />
                  </svg>
                  <span className={styles.errorBannerText}>{apiError}</span>
                  <button
                    className={styles.errorBannerClose}
                    onClick={() => setApiError("")}
                    aria-label="Dismiss"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Stats */}
              {searched && !loading && !apiError && (
                <div className={styles.statsBar}>
                  <span className={styles.resultCount}>
                    Showing <strong>{results.length}</strong>
                    {totalResults > 0 && (
                      <>
                        {" "}
                        of <strong>{totalResults}</strong>
                      </>
                    )}{" "}
                    results
                    {searchQuery && (
                      <>
                        {" "}
                        for "<strong>{searchQuery}</strong>"
                      </>
                    )}
                  </span>
                  {results.length > 0 && (
                    <span className={styles.badge}>CDP Match</span>
                  )}
                </div>
              )}
            </div>

            {/* Results */}
            <div className={styles.tableWrapper}>
              {!searched && !loading && (
                <div className={styles.emptyState}>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8a8f9e"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <p className={styles.emptyTitle}>Search your customers</p>
                  <p className={styles.emptyDesc}>
                    Select an event above to find matching customers
                  </p>
                </div>
              )}

              {loading && (
                <div className={styles.cardGrid}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className={styles.skeletonCard}>
                      <div className={styles.skeletonAvatar} />
                      <div className={styles.skeletonLineWrap}>
                        <div
                          className={styles.skeletonLine}
                          style={{ width: "70%" }}
                        />
                        <div
                          className={styles.skeletonLine}
                          style={{ width: "45%" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {searched && !loading && !apiError && results.length === 0 && (
                <div className={styles.emptyState}>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8a8f9e"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <p className={styles.emptyTitle}>No results found</p>
                  <p className={styles.emptyDesc}>
                    Try a different event{searchQuery ? " or search term" : ""}
                  </p>
                </div>
              )}

              {!loading && results.length > 0 && (
                <div className={styles.cardGrid}>
                  {results.map((item) => (
                    <div
                      key={item._id}
                      className={styles.resultCard}
                      onClick={() => setSelectedItem(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setSelectedItem(item);
                        }
                      }}
                    >
                      <div className={styles.resultCardHeader}>
                        <div className={styles.avatar}>
                          {getInitials(item.name || item.email)}
                        </div>
                        <div className={styles.resultCardTitle}>
                          <div className={styles.nameText}>
                            {item.name || "—"}
                          </div>
                          <div className={styles.resultCardEmail}>
                            {item.email || "—"}
                          </div>
                        </div>
                      </div>

                      <div className={styles.resultCardTags}>
                        {/* <span className={styles.chip}>
                          {[item.city, item.country]
                            .filter((v) => v && v !== "NA")
                            .join(", ") || "—"}
                        </span> */}
                        {item.user_type && (
                          <span className={styles.chip}>{item.user_type}</span>
                        )}
                        {item.event_name && (
                          <span className={styles.chip}>{item.event_name}</span>
                        )}
                      </div>

                      <div className={styles.resultCardFooter}>
                        <span className={styles.resultCardFooterItem}>
                          {isEmpty(item.mobile)
                            ? "No mobile on file"
                            : item.mobile}
                        </span>
                        <span className={styles.resultCardFooterItem}>
                          {item.event_source || "—"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {results.length > 0 && (
                <div ref={loaderRef} className={styles.sentinel}>
                  {loadingMore && (
                    <div className={styles.loadingMoreRow}>
                      <span className={styles.spinnerDark} />
                      <span className={styles.loadingMoreText}>
                        Loading more results...
                      </span>
                    </div>
                  )}
                  {!hasMore && results.length > 0 && searched && (
                    <div className={styles.endOfResults}>
                      ✓ All {totalResults || results.length} results loaded
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {selectedItem && (
        <CustomerModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
      {showInfo && <InfoPopover onClose={() => setShowInfo(false)} />}
    </div>
  );
};

export default CdpSearch;